/**
 * Recording bitrate to disk space.
 *
 * One number, because it was previously written out three times, in
 * `bomCalculator`, `nvrSlug` and the public storage calculator, and all three
 * carried the same unit error.
 *
 * The error: `Mbps × 86400 / 8 / 1024`. That mixes bases. A megabit per second
 * is decimal, 10⁶ bit/s, so dividing by 8 gives decimal megabytes; dividing
 * those by 1024 converts as if they were mebibytes. The result, 10.547 per Mbps
 * per day, is neither of the two units anyone actually reads:
 *
 *   decimal GB (what a drive's label says)  10.800
 *   binary  GiB (what the OS reports)       10.058
 *   the old figure                          10.547
 *
 * It sat 2.3% under the labelled capacity, and the BOM then buys drives whose
 * capacity is quoted decimally, so the shortfall is real rather than cosmetic:
 * 64 cameras at 4 Mbps over 30 days came out as 81.00 TB against the 82.94 TB
 * of disk that has to be purchased. Always short, never over.
 *
 * Storage figures are therefore decimal here, matching how drives are sold and
 * how NVR datasheets quote capacity.
 */

/** Seconds in a day. */
const SECONDS_PER_DAY = 86_400;

/**
 * Gigabytes (decimal, 10⁹ bytes) recorded per day at a sustained bitrate.
 *
 * @param bitrateMbps sustained recording bitrate in Mbit/s
 * @param hoursPerDay recording hours per day; 24 for continuous
 */
export function storageGbPerDay(bitrateMbps: number, hoursPerDay = 24): number {
  if (!Number.isFinite(bitrateMbps) || bitrateMbps <= 0) return 0;
  const seconds = (SECONDS_PER_DAY * Math.max(0, Math.min(24, hoursPerDay))) / 24;
  // Mbit/s × s = Mbit; ÷8 = MB; ÷1000 = GB. All decimal.
  return (bitrateMbps * seconds) / 8 / 1000;
}

/** Decimal GB per Mbit/s of continuous recording per day. Exactly 10.8. */
export const GB_PER_MBPS_PER_DAY = storageGbPerDay(1);

/**
 * Decimal GB (10⁹ bytes) to binary TiB (2⁴⁰ bytes).
 *
 * Everything above is decimal, because that is how footage volume is quoted and
 * how drives are labelled. `recommendRaid5Storage` is the one consumer that is
 * NOT: it compares the requirement against each drive's REAL capacity, so it
 * takes binary TiB, and handing it a decimal TB number inflates the requirement
 * by 9.95% and buys drives nobody needs.
 *
 * That is not hypothetical. When the storage chain moved to decimal, the three
 * callers of the sizer kept passing their decimal figure straight through, and
 * ten cameras at 4 Mbps over 30 days started recommending 3x 16TB where the
 * byte-correct answer is 3x 14TB. This function exists so the conversion is
 * written once and is obvious at every call site.
 */
export function decimalGbToBinaryTib(gb: number): number {
  if (!Number.isFinite(gb) || gb <= 0) return 0;
  return (gb * 1e9) / 2 ** 40;
}
